import ImageProxy from '@next-image-proxy';

import { Card } from '@uikit';

import { useFeatureTogglesQuery } from '@query';

import { MIXPANEL_EVENT_NAMES } from '@shared/api/mixPanel';
import { HotOffer } from '@shared/api/types/hotOffers';
import KarmaTag from '@shared/common/components/KarmaTag';
import NextLink from '@shared/common/components/NextLink';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useMixPanel } from '@shared/common/providers/MixPanelProvider';
import { membershipTiers } from '@shared/constants/membershipTiers';

import HotOfferShareButton from '@store:web/components/pages/HotOffersPage/HotOfferShareButton';
import HotOfferStats from '@store:web/components/pages/HotOffersPage/HotOfferStats';

export interface HotOfferCardProps {
  offer: HotOffer;
  imagePriority?: boolean;
}

export default function HotOfferCardLite({ offer, imagePriority }: HotOfferCardProps) {
  const {
    id,
    attributes: {
      name = 'Unnamed offer',
      offer: description = 'Nothing to describe',
      icon = '',
      banner = '',
      accessTier = 'FREE',
      claimKarmaThreshold = 0,
    } = {},
  } = offer;
  const { data: { hotOffersSharing, membership } = {} } = useFeatureTogglesQuery();
  const { user } = useAuth();
  const { track } = useMixPanel();

  const handleTrackedLinkClick = () => {
    if (user?.id) {
      track(MIXPANEL_EVENT_NAMES.HOT_OFFER_VIEW_BUTTON_CLICK, {
        distinct_id: user.id,
        offer: name,
      });
    }
  };

  return (
    <Card className="flex flex-col rounded-[24px] overflow-hidden !shadow-header !p-0">
      <ImageProxy
        className="w-full object-cover aspect-[8/3]"
        src={banner}
        alt={`${name} Magic Store Hot Offer`}
        width={800}
        height={300}
        priority={imagePriority}
        sizes="(min-width: 568px) 50vw, (min-width: 1280px) 33vw, 100vw"
        data-qa="hot-offer-container-banner"
      />

      <div className="h-full flex flex-col gap-2 p-4">
        <div className="flex-grow flex flex-col divide-y divide-primary-200">
          <div className="flex gap-3 md:gap-5 min-h-[4rem] items-center pb-2">
            <ImageProxy
              className="w-[50px] rounded-full border border-primary-200 flex-shrink-0"
              src={icon}
              alt={`${name} Magic Store Hot Offer`}
              width={50}
              height={50}
              priority={imagePriority}
              data-qa="hot-offer-container-icon"
            />

            <span
              className="font-semibold md:text-lg line-clamp-2 hyphens-none lg:text-xl"
              data-qa="hot-offer-container-name"
            >
              {name}
            </span>
          </div>

          <div className="py-2">
            <span
              className="text-neutral-500 h-12 line-clamp-2 hyphens-auto"
              data-qa="hot-offer-container-offer"
            >
              {description}
            </span>
          </div>

          <div className="flex flex-wrap justify-between gap-1 py-2 overflow-hidden">
            <span className="flex flex-1 items-center text-neutral-500">Min. Karma:</span>
            <span className="flex items-center justify-center gap-1 text-secondary-700">
              <KarmaTag points={claimKarmaThreshold} />
              Karma
            </span>
          </div>

          {!!membership && (
            <div className="flex flex-wrap justify-between gap-1 py-2 overflow-hidden">
              <span className="flex flex-1 items-center text-neutral-500">Min. Membership:</span>
              <span className="flex items-center justify-center gap-1 text-secondary-700">
                <div className="text-xs py-1 px-2 border border-secondary bg-secondary-200 rounded-full">
                  {membershipTiers?.[accessTier]?.name}
                </div>
              </span>
            </div>
          )}

          <HotOfferStats offer={offer} className="py-3 mt-auto" />
        </div>

        <div className="flex items-center gap-2">
          <NextLink
            href={`/hot-offers/${id}`}
            className="button button-solid-secondary button-xl w-full"
            data-qa="hot-offer-view-button"
            onClick={handleTrackedLinkClick}
          >
            View Offer
          </NextLink>

          {hotOffersSharing && <HotOfferShareButton offer={offer} />}
        </div>
      </div>
    </Card>
  );
}
